import React, { useRef } from "react";
import { Modal, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import { getClientDisplayName } from "../utils/constants";
import { convertToArabicDate, getMonthYearInFrench } from "../utils/date";
import { getPaymentMethodInFrench } from "../utils/payment";
import LogoStationnement from "../assets/images/logo-stationnement.svg";
import PermissionRender from "./PermissionRender";

const SubscriptionReceipt80mm = ({
  subscriptionData,
  visible,
  onClose,
  paymentId,
}) => {
  const pdfRef = useRef();

  const pageStyle = `
    @page {
      size: 80mm auto;
      margin: 0;
    }
    @media print {
      html, body {
        width: 80mm;
        margin: 0 !important;
        padding: 0 !important;
        font-size: 10px !important;
        font-family: Arial, sans-serif !important;
      }
      .printable-area-80mm {
        width: 76mm !important;
        margin: 0 auto !important;
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        font-size: 12px !important;
        line-height: 1.2 !important;
        font-family: Arial, sans-serif !important;
      }
      .no-print {
        display: none !important;
      }
      .modal-content {
        display: none !important;
      }
    }
  `;

  const printNow = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: "Recu_Paiement_Abonnement",
    pageStyle: pageStyle,
  });

  const handlePrint = () => {
    setTimeout(() => {
      if (pdfRef.current) {
        printNow();
      } else {
        console.error("PDF reference is not available.");
      }
    }, 200);
  };

  // Find payment details
  const payment =
    subscriptionData?.payments?.find((p) => p.id === paymentId) || null;
  const echeancePayment =
    payment?.details?.find((it) => it.type === "echeance") || null;
  const registration_fee =
    payment?.details?.find((it) => it.type === "registration_fee") || null;
  const reactivation_fee =
    payment?.details?.find((it) => it.type === "reactivation_fee") || null;
  const macaron_fee =
    payment?.details?.find((it) => it.type === "macaron_fee") || null;
  const echeanceDetail =
    subscriptionData?.echeances?.find(
      (it) => it.id === echeancePayment?.echeance_id
    ) || null;

  // Helper function to get agent name based on payment source
  const getAgentName = (payment) => {
    const source = payment?.source;
    
    if (source === "citizen_mobile") return "Mobile citoyen";
    if (source === "citizen_web") return "Web citoyen";
    
    // For agent or any other source, try to get the user's full_name
    if (payment?.user?.full_name) return payment.user.full_name;
    
    return "N/A";
  };

  // Determine if this is an agent payment (show "Nom Agent :" prefix)
  const isAgentPayment = payment?.source === "agent" || payment?.user?.full_name;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      // width="350px"
      centered
    >
      <div className="mb-4 text-left no-print">
        <PermissionRender requiredPermission={["subscriptions", "print_receipt"]}>
        <Button
          onClick={handlePrint}
          type="primary"
          icon={<DownloadOutlined />}
        >
          Imprimer le reçu / طباعة الوصل
        </Button>
        </PermissionRender>
      </div>
      <div
        ref={pdfRef}
        className="printable-area-80mm bg-white text-black"
        style={{
          fontFamily: "Arial, sans-serif",
          fontSize: "10px",
          margin: "0 auto",
          padding: "6px",
          border: "1px solid #000",
          backgroundColor: "white",
          boxSizing: "border-box",
          width: "72mm",
          minHeight: "100mm",
          maxWidth: "350px",
        }}
      >
        <div
          style={{ textAlign: "center", marginBottom: 6 }}
          className="flex justify-between"
        >
          <div>
            <div style={{ fontWeight: "bold" }}>VILLE DE TANGER</div>
            <div style={{ fontWeight: "bold" }}>SERVICE DE STATIONNEMENT</div>
          </div>
          <div>
            <div style={{ fontWeight: "bold" }}>مدينة طنجة</div>
            <div style={{ fontWeight: "bold" }}>خدمة الركن</div>
          </div>
        </div>
        <div style={{ borderBottom: "1px dashed #000", marginBottom: 6 }}></div>

        <div style={{ textAlign: "center", marginBottom: 6 }}>
          <div style={{ fontWeight: "bold", textDecoration: "underline" }}>
            {macaron_fee && !echeancePayment && !registration_fee && !reactivation_fee 
              ? "REÇU DE PAIEMENT - FRAIS DE MACARON"
              : "REÇU DE PAIEMENT D'ABONNEMENT"
            }
          </div>
          <div style={{ fontWeight: "bold", fontSize: 14 }}>
            {macaron_fee && !echeancePayment && !registration_fee && !reactivation_fee 
              ? "وصل أداء - رسوم الماكرون"
              : "وصل أداء الاشتراك"
            }
          </div>
        </div>
        <div style={{ borderBottom: "1px dashed #000", marginBottom: 6 }}></div>

        <div style={{ textAlign: "center", marginBottom: 6, fontSize: 12 }}>
          <strong>REÇU N° : {payment?.reference || "N/A"}</strong>
        </div>
        <div style={{ marginBottom: 6 }}>
          <div
            style={{
              fontWeight: "bold",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <span>Informations de l'abonné(e)</span>
            <span style={{ fontWeight: "bold" }}>: معلومات المشترك(ة)</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Nom de l'abonné(e) : </span>
            <span style={{ fontWeight: "bold" }}>: اسم المشترك(ة) </span>
          </div>
          <div className="text-center" style={{ marginBottom: 6 }}>
            <strong>{getClientDisplayName(subscriptionData?.client)}</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Numéro d'immatriculation : </span>
            <span style={{ fontWeight: "bold" }}>: رقم التسجيل </span>
          </div>
          <div className="text-center" style={{ marginBottom: 6 }}>
            <strong style={{ direction: "ltr", unicodeBidi: "embed" }}>
              {subscriptionData?.vehicle?.matricule || "N/A"}
            </strong>
          </div>
        </div>
        <div style={{ borderBottom: "1px dashed #000", marginBottom: 6 }}></div>
        <div style={{ marginBottom: 6 }}>
          <div
            style={{
              fontWeight: "bold",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <span>Détails d'abonnement</span>
            <span style={{ fontWeight: "bold" }}>: تفاصيل الاشتراك</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Numéro du contrat : </span>
            <span style={{ fontWeight: "bold" }}>: رقم العقد </span>
          </div>
          <div className="text-center" style={{ marginBottom: 6 }}>
            <strong>{subscriptionData?.contract_number || "N/A"}</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Formule d'abonnement : </span>
            <span style={{ fontWeight: "bold" }}>: صيغة الاشتراك </span>
          </div>
          <div className="text-center" style={{ marginBottom: 6 }}>
            <strong>
              {subscriptionData?.subscription_type?.name || "N/A"}
            </strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Période de validité : </span>
            <span style={{ fontWeight: "bold" }}>: مدة الاشتراك </span>
          </div>
          <div className="text-center" style={{ marginBottom: 6 }}>
            <strong>
              [
              {subscriptionData?.start_date
                ? subscriptionData.start_date.split("-").reverse().join("/")
                : "N/A"}{" "}
              à{" "}
              {subscriptionData?.end_date
                ? subscriptionData?.end_date.split("-").reverse().join("/")
                : "N/A"}
              ]
            </strong>
          </div>
        </div>
        <div style={{ borderBottom: "1px dashed #000", marginBottom: 6 }}></div>
        <div style={{ marginBottom: 6 }}>
          <div
            style={{
              fontWeight: "bold",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <span>Détails de paiement : </span>
            <span style={{ fontWeight: "bold" }}>: تفاصيل الدفع</span>
          </div>
          {/* Tarif appliqué - only show if there's an echeance payment */}
          {echeancePayment && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                <span style={{ textAlign: "left", flex: 1 }}>Tarif appliqué :</span>
                <span
                  style={{
                    textAlign: "right",
                    flex: 1,
                    direction: "rtl",
                    fontWeight: "bold",
                  }}
                >
                  التسعيرة المطبقة {" : "}
                </span>
              </div>
              <div
                style={{ textAlign: "center", fontWeight: "bold", marginBottom: 6 }}
              >
                [{echeancePayment?.amount} DHS]
              </div>
            </>
          )}

          {/* Frais d'inscription */}
          {registration_fee && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                <span style={{ textAlign: "left", flex: 1 }}>
                  {" "}
                  Frais d'inscription:
                </span>
                <span
                  style={{
                    textAlign: "right",
                    flex: 1,
                    direction: "rtl",
                    fontWeight: "bold",
                  }}
                >
                  رسوم التسجيل {" : "}
                </span>
              </div>
              <div
                style={{
                  textAlign: "center",
                  fontWeight: "bold",
                  marginBottom: 6,
                }}
              >
                [{registration_fee?.amount} DHS]
              </div>
            </>
          )}
          {/* Frais de réactivation */}
          {reactivation_fee && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                <span style={{ textAlign: "left", flex: 1 }}>
                  {" "}
                  Frais de réactivation:
                </span>
                <span
                  style={{
                    textAlign: "right",
                    flex: 1,
                    direction: "rtl",
                    fontWeight: "bold",
                  }}
                >
                  رسوم إعادة التفعيل {" : "}
                </span>
              </div>
              <div
                style={{
                  textAlign: "center",
                  fontWeight: "bold",
                  marginBottom: 6,
                }}
              >
                [{reactivation_fee?.amount} DHS]
              </div>
            </>
          )}

          {/* Frais de macaron */}
          {macaron_fee && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                <span style={{ textAlign: "left", flex: 1 }}>
                  {" "}
                  Frais de changement de macaron:
                </span>
                <span
                  style={{
                    textAlign: "right",
                    flex: 1,
                    direction: "rtl",
                    fontWeight: "bold",
                  }}
                >
                  رسوم تغيير الماكرون {" : "}
                </span>
              </div>
              <div
                style={{
                  textAlign: "center",
                  fontWeight: "bold",
                  marginBottom: 6,
                }}
              >
                [{macaron_fee?.amount} DHS]
              </div>
            </>
          )}

          {/* Montant payé */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <span style={{ textAlign: "left", flex: 1 }}>Montant payé :</span>
            <span
              style={{
                textAlign: "right",
                flex: 1,
                direction: "rtl",
                fontWeight: "bold",
              }}
            >
              المبلغ المدفوع {" : "}
            </span>
          </div>
          <div
            style={{ textAlign: "center", fontWeight: "bold", marginBottom: 6 }}
          >
            {payment.amount} DHS
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <span style={{ textAlign: "left", flex: 1 }}>
              Dont TVA (20 %) :
            </span>
            <span
              style={{
                textAlign: "right",
                direction: "rtl",
                fontWeight: "bold",
              }}
            >
              منها ضريبة على القيمة المضافة (%20) {" : "}
            </span>
          </div>
          <div
            style={{ textAlign: "center", fontWeight: "bold", marginBottom: 6 }}
          >
            {Math.round(((payment.amount * 20) / 120) * 100) / 100} DHS
          </div>
          {/* Mensualité - only show if there's an echeance payment */}
          {echeancePayment && echeanceDetail && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                <span style={{ textAlign: "left", flex: 1 }}>Mensualité : </span>
                <span
                  style={{
                    textAlign: "right",
                    flex: 1,
                    direction: "rtl",
                    fontWeight: "bold",
                  }}
                >
                  الدفعة الشهرية {" : "}
                </span>
              </div>
              <div
                style={{ textAlign: "center", fontWeight: "bold", marginBottom: 6 }}
              >
                {getMonthYearInFrench(echeanceDetail?.start_date)}
              </div>
            </>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <span style={{ textAlign: "left", flex: 1 }}>
              Mode de paiement :{" "}
            </span>
            <span
              style={{
                textAlign: "right",
                flex: 1,
                direction: "rtl",
                fontWeight: "bold",
              }}
            >
              طريقة الدفع {" : "}
            </span>
          </div>
          <div
            style={{ textAlign: "center", fontWeight: "bold", marginBottom: 6 }}
          >
            {getPaymentMethodInFrench(payment?.payment_method) || "N/A"}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Date de paiement :</span>
            <span style={{ direction: "rtl", fontWeight: "bold" }}>
              تاريخ الدفع{" : "}
            </span>
          </div>
          <div className="text-center" style={{ marginBottom: 6 }}>
            <strong>{payment.payment_date}</strong>
          </div>
        </div>
        <div style={{ borderBottom: "1px dashed #000", marginBottom: 6 }}></div>
        {/* Validity section - only show for echeance payments */}
        {echeancePayment && echeanceDetail && (
          <>
            <div style={{ marginBottom: 6 }}>
              <strong>
                Valable jusqu'au {echeanceDetail?.end_date} à 23:59
                <br />
                <div className="text-right" style={{ direction: "rtl" }}>
                  صالح حتى {convertToArabicDate(echeanceDetail?.end_date)} على
                  الساعة 23:59
                </div>
              </strong>
            </div>
            <div style={{ borderBottom: "1px dashed #000", marginBottom: 6 }}></div>
          </>
        )}
        <div>
          <div className="flex justify-between">
            <div>Patente : 33001036</div>
            <div>ICE : 000831140000073</div>
          </div>
          <div>{isAgentPayment ? `Nom Agent : ${getAgentName(payment)}` : getAgentName(payment)}</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <img
            src={LogoStationnement}
            alt="Logo Stationnement"
            style={{ width: "160px", height: "160px" }}
          />
        </div>
        <div style={{ textAlign: "center", fontSize: 12, marginBottom: 6 }}>
          <div>
            <strong>SERVICE CLIENT / خدمة الزبناء</strong>
          </div>
          <div>
            <strong>05 39 32 51 32/33</strong>
          </div>
          <div>
            <strong>MERCI POUR VOTRE FIDÉLITÉ.</strong>
          </div>
          <div>
            <strong>شكرا لوفائكم</strong>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SubscriptionReceipt80mm;
